import { bootstrapApplication } from '@angular/platform-browser';
import { environment } from './environments/environment';
import { Settings } from 'luxon';
import * as Sentry from '@sentry/angular';

function isUnhandledAngularHttpError(event: {
  exception?: { values?: Array<{ mechanism?: { type?: string }; value?: string }> };
  message?: string;
}): boolean {
  const hasAngularErrorHandlerMechanism = event.exception?.values?.some(
    (exception) => exception.mechanism?.type === 'auto.function.angular.error_handler',
  );
  if (!hasAngularErrorHandlerMechanism) {
    return false;
  }

  const message = event.message ?? event.exception?.values?.[0]?.value ?? '';
  return message.startsWith('Http failure response for ');
}

/** Deep-merges `source` into `target` like lodash `merge`: plain objects recurse, arrays merge by index. */
function mergeDeep(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): void {
  for (const [key, value] of Object.entries(source)) {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue;
    }
    const current = target[key];
    const bothObjects =
      typeof value === 'object' &&
      value !== null &&
      typeof current === 'object' &&
      current !== null &&
      Array.isArray(value) === Array.isArray(current);
    if (bothObjects) {
      mergeDeep(current as Record<string, unknown>, value as Record<string, unknown>);
    } else if (value !== undefined) {
      target[key] = value;
    }
  }
}

function extendConfiguration(environment: Record<string, unknown>): Promise<void> {
  return new Promise((resolve, reject) => {
    const httpRequest = new XMLHttpRequest();
    httpRequest.onreadystatechange = () => {
      if (httpRequest.readyState === XMLHttpRequest.DONE) {
        if (httpRequest.status === 200) {
          const environmentSettings = JSON.parse(httpRequest.responseText);
          mergeDeep(environment, environmentSettings);
          resolve();
        } else {
          reject(new Error('there was a problem with loading the environment settings'));
        }
      }
    };
    httpRequest.open('GET', 'settings.json');
    httpRequest.send();
  });
}

extendConfiguration(environment)
  .then(async () => {
    try {
      if (environment.sentry.dsn) {
        Sentry.init({
          dsn: environment.sentry.dsn,
          environment: environment.envName,
          release: environment.version,
          beforeSend(event) {
            // Keep normalized interceptor events and drop duplicate generic Angular HTTP reports.
            if (isUnhandledAngularHttpError(event)) {
              return null;
            }
            return event;
          },
        });
      }

      const { appConfig } = await import('./app/app.config');
      const { AppComponent } = await import('./app/app.component');

      Settings.defaultLocale = 'de-CH';
      bootstrapApplication(AppComponent, appConfig).catch((err) => {
        console.error(err);
        if (environment.sentry?.dsn) {
          Sentry.captureException(err);
        }
      });
    } catch (err) {
      console.error('Bootstrap error in .then block:', err);
      if (environment.sentry?.dsn) {
        Sentry.captureException(err);
      }
    }
  })
  .catch((err) => {
    console.error('Bootstrap error:', err);
    if (environment.sentry?.dsn) {
      Sentry.captureException(err);
    }
  });
